import { toRaw } from 'vue';
import { isEmptyGraph } from '../graph/graphModel.js';
import { deserializeGraph, serializeGraph } from '../graph/graphSerialization.js';
import { getHassNodeSchema } from '../Hasp/properties/v2/hass/index.js';

/**
 * Adapter between Designer pages (Konva) and the pure Home Assistant flow
 * model: storage only, so HaspPage can import it without the compiler (see
 * hassEffective.js for what a page exports).
 *
 * A page holds its flow graph in `page.hass` (in-memory form, uuid
 * references). A loaded design keeps the file form in `page._pendingHass`
 * until all of the page's objects exist, then restoreLayerHass() resolves it.
 * Pages often arrive through Vue-reactive state; every entry point unwraps
 * them, because the graph is plain data that gets structuredClone'd.
 */
const isPageObject = (page, node) => node !== page && typeof node?.obj === 'string' &&
    node.haspid !== undefined && node.haspid !== null && typeof node.id === 'function';

/** The UI objects on `page` (the page itself is no Home Assistant source). */
export function pageHassObjects(page) {
    page = toRaw(page);
    return page?.find?.(node => isPageObject(page, node)) ?? [];
}

/** Lookup context for hassGraph/hassCompiler: resolves only objects on `page`. */
export function createPageHassContext(page) {
    page = toRaw(page);
    const byUuid = new Map(pageHassObjects(page).map(node => [node.id(), node]));
    return { getObject: uuid => byUuid.get(uuid) ?? null, pageId: page?.haspid ?? null };
}

const FILE_OPTIONS = Object.freeze({ getNodeSchema: getHassNodeSchema, label: 'Home Assistant', objectTargets: true });

/** Design-save form of a page's flow graph, or undefined when there is none. */
export function exportPageHass(page) {
    page = toRaw(page);
    if (page?.hass && !isEmptyGraph(page.hass)) {
        const context = createPageHassContext(page);
        return serializeGraph(page.hass, uuid => context.getObject(uuid)?.haspid);
    }
    return page?._pendingHass ?? undefined;
}

/** File form → in-memory graph, objects resolved by `uuidOfHaspid`. */
export function deserializeHassGraph(data, uuidOfHaspid) {
    const { graph, diagnostics } = deserializeGraph(data, uuidOfHaspid, FILE_OPTIONS);
    return { graph: withActionTargetsOut(graph), diagnostics };
}

/**
 * Early flow graphs (before release) wired an action's target from the
 * entity's `state` output into the action; it is the action's `target`
 * output into the entity now.
 */
function withActionTargetsOut(graph) {
    const kind = id => graph.nodes.find(node => node.id === id)?.kind;
    return {
        ...graph,
        wires: graph.wires.map(wire => (wire.to.port === 'target' && kind(wire.to.node) === 'action' && kind(wire.from.node) === 'entity'
            ? { ...wire, from: { node: wire.to.node, port: 'target' }, to: { node: wire.from.node, port: 'target' } }
            : wire)),
    };
}

/** Resolve a page's pending file-form flow graph against its loaded objects. */
export function restorePageHass(page) {
    page = toRaw(page);
    if (!page?._pendingHass) return [];
    const uuidByHaspid = new Map(pageHassObjects(page).map(node => [Number(node.haspid), node.id()]));
    const { graph, diagnostics } = deserializeHassGraph(page._pendingHass, haspid => uuidByHaspid.get(haspid));
    page.hass = graph;
    page._pendingHass = null;
    return diagnostics.map(message => `Page ${page.haspid}: ${message}`);
}

export function restoreLayerHass(layer) {
    return (toRaw(layer)?.getChildren?.() ?? [])
        .filter(page => page?._pendingHass)
        .flatMap(restorePageHass);
}

/**
 * The page (or template page) a UI object is on, unwrapped. Objects added
 * through the app's reactive layer have a Vue proxy as Konva parent.
 */
export function findHassPage(node) {
    let current = toRaw(toRaw(node)?.getParent?.());
    while (current && current.obj !== 'page' && current.obj !== 'template_page') current = toRaw(current.getParent?.());
    return current ?? null;
}

/** Whether a UI object takes part in its page's Home Assistant flow graph (a wire at it). */
export function hasHassFlow(node) {
    const raw = toRaw(node);
    const uuid = raw?.id?.();
    const graph = uuid ? findHassPage(raw)?.hass : null;
    return Boolean(graph?.wires?.some(wire => wire.from.object === uuid || wire.to.object === uuid));
}
